import React, { useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { recuperarDetallDiaComplet } from '../../actions/controlPresenciaActions';
import { recuperarDetallMesComplet } from '../../actions/configCalendariAction';
import { TipusFixatge } from '../ControlPresencia/TipusFixatge';
import { TipusIncidencia } from '../ControlPresencia/TipusIncidencia';
import { ResumHoresDiaConsultat } from './ResumHoresDiaConsultat';
import { FormulariIncidencia } from '../Incidencia/FormulariIncidencia';
import { FormulariIncidenciaRestaHores } from '../Incidencia/FormulariIncidenciaRestaHores';
import { FormulariIncidenciaRestaHoresFiJornada } from '../Incidencia/FormulariIncidenciaRestaHoresFiJornada';
import {
  esborrarErrorAction, esborrarErrorOkAction, esborrarOkAction,
  uiAmagarCalendarModal, uiAmagarDiaConsultatAction,
  uiAmagarIncidenciesAction, uiAmagarIncidenciesRestaHoresAction,
  uiAmagarIncidenciesRestaHoresFiJornadaAction,
  uiMostraIncidenciesAction, uiMostraIncidenciesRestaHoresAction,
  uiMostraIncidenciesRestaHoresFiJornadaAction,
} from '../../actions/ui';
import moment from 'moment-timezone';
import Moment from 'react-moment';
import SpinerLoader from '../../Helpers/SpinerLoader';

// ESTRATÈGIA (0 crides HTTP addicionals):
// Des de SETMANA  → cp.detallSetmana.fixatges_setmanals (ja té fixatges individuals)
// Des de CALENDARI → calendari.detallMes.fixatgesMes (ara també té fixatges individuals
//                   gràcies al patch PHP de getFixatgesEntreDatesSimplificat)
// Post-guardat    → re-fetch dirigit per actualitzar

export const DetallDiaScreen = ({ dataAvui }) => {
  const { t } = useTranslation('generic');
  const dispatch = useDispatch();

  const { auth, cp, ui, calendari } = useSelector(
    state => ({ auth: state.auth, cp: state.cp, ui: state.ui, calendari: state.calendari }),
    shallowEqual
  );

  const { detallSetmana } = cp;
  const { incidencia, diaConsultat, modalCalendar, incidenciaRestaHores, msgOk, incidenciaRestaHoresFiJornada } = ui;
  const diaKey = diaConsultat?.dia ?? null;

  // ── Re-fetch post-guardat ─────────────────────────────────────────────────
  const prevMsgOk = useRef(msgOk);
  const msgOkCanviat = msgOk !== prevMsgOk.current && !!msgOk;
  useEffect(() => { prevMsgOk.current = msgOk; });

  useEffect(() => {
    if (!msgOkCanviat || !diaKey || !auth.tokenJwt) return;
    if (diaASetmana) {
      // Des de setmana: actualitzar el dia concret al store (0 HTTP extra per CalendarScreen)
      dispatch(recuperarDetallDiaComplet(auth.tokenJwt, diaKey));
    }
    // Des del calendari: NO fem res aquí.
    // CalendarScreen detecta msgOk + modal tancat i fa el re-fetch del mes.
    // Així evitem doble fetch i el calendari es refresca correctament.
  }, [msgOkCanviat]);

  // ── Detectar font de dades ────────────────────────────────────────────────
  const diaASetmana = useMemo(() => {
    if (!detallSetmana?.fixatges_setmanals || !diaKey) return false;
    return detallSetmana.fixatges_setmanals.some(d => d.dia === diaKey);
  }, [detallSetmana, diaKey]);

  // ── Llegir detallDia del store (0 HTTP) ───────────────────────────────────
  const detallDia = useMemo(() => {
    if (!diaKey) return null;
    if (diaASetmana) {
      return detallSetmana.fixatges_setmanals.find(d => d.dia === diaKey) ?? null;
    }
    // Calendari: llegir de detallMes (ara inclou fixatges individuals)
    return calendari.detallMes?.fixatgesMes?.find(d => d.dia === diaKey) ?? null;
  }, [diaASetmana, detallSetmana, calendari.detallMes, diaKey]);

  // Dia no trobat → construir objecte buit (dia futur sense fixatges)
  const detallDiaFinal = detallDia ?? {
    dia: diaKey,
    fixatges: [],
    esLaborable: [1,2,3,4,5].includes(new Date((diaKey ?? '') + 'T00:00:00').getDay()),
    idTipusAlerta: 0,
    existeixAlerta: false,
  };

  // ── Incidències del dia ───────────────────────────────────────────────────
  const incidenciesDia = useMemo(() => {
    if (!diaKey) return [];
    const llistat = diaASetmana
      ? detallSetmana?.llistatIncidencies
      : calendari.detallMes?.incidenciesMes;
    if (!llistat) return [];
    return llistat.filter(e => {
      const inici = moment(e.DataInici_IncidenciaFix).format('YYYY-MM-DD');
      const fi    = moment(e.DataFi_IncidenciaFix).format('YYYY-MM-DD');
      return diaKey >= inici && diaKey <= fi;
    });
  }, [diaASetmana, detallSetmana, calendari.detallMes, diaKey]);

  // ── Flags ─────────────────────────────────────────────────────────────────
  const { diaConsultatAlerta, algunFixatge, mesFixatges } = useMemo(() => {
    if (!detallDiaFinal) return { diaConsultatAlerta: false, algunFixatge: false, mesFixatges: false };
    return {
      diaConsultatAlerta: detallDiaFinal.esLaborable ? detallDiaFinal.idTipusAlerta : 'capSetm',
      algunFixatge:  (detallDiaFinal.fixatges?.length ?? 0) > 0,
      mesFixatges:   (detallDiaFinal.fixatges?.length ?? 0) >= 2,
    };
  }, [detallDiaFinal]);

  // ── Llistes ───────────────────────────────────────────────────────────────
  const listItems = useMemo(() => (
    detallDiaFinal?.fixatges?.map(d => (
      <li className='list-checked-item' key={d.IdFixatge}>
        <TipusFixatge idTipusFixatge={Number(d.IdTipus_Fixatge)} idTipusIncidencia={Number(d.IdTipusIncidencia_Fixatge)} />
        {' - '}
        <Moment format="HH:mm" date={d.DataHora_Fixatge} />
      </li>
    )) ?? []
  ), [detallDiaFinal?.fixatges]);

  const listInci = useMemo(() => (
    incidenciesDia.map(d => (
      <li className='list-checked-item' key={d.IdIncidenciaFix}>
        <TipusIncidencia IdTipusIncidencia_IncidenciaFix={Number(d.IdTipusIncidencia_IncidenciaFix)} />
        {' - '}
        {moment(d.DataFi_IncidenciaFix).diff(moment(d.DataInici_IncidenciaFix), 'days') >= 1 ? (
          <>{moment(d.DataInici_IncidenciaFix).format('DD MMM  HH:mm')} - {moment(d.DataFi_IncidenciaFix).format('DD MMM  HH:mm')}</>
        ) : (
          <><Moment format="HH:mm" date={d.DataInici_IncidenciaFix} /> - <Moment format="HH:mm" date={d.DataFi_IncidenciaFix} /></>
        )}
        {d.ValidatResponsable_IncidenciaFix === '1' && d.Justificat_IncidenciaFix === '1' ? (
          <p><span className='badge bg-success'>Validat i justificat</span></p>
        ) : d.ValidatResponsable_IncidenciaFix === '1' && d.Justificat_IncidenciaFix !== '0' ? (
          <p><span className='badge bg-warning'>Validat per responsable</span></p>
        ) : d.Justificat_IncidenciaFix === '0' ? (
          <p><span className='badge bg-danger'>No validat</span></p>
        ) : (
          <p><span className='badge bg-danger'>Pendent de validacio</span></p>
        )}
      </li>
    ))
  ), [incidenciesDia]);

  const { hores, minuts } = useMemo(() => {
    const lastFixatge = detallDiaFinal?.fixatges?.[detallDiaFinal.fixatges.length - 1]?.DataHora_Fixatge;
    if (lastFixatge) {
      const mins = moment(lastFixatge).add(1, 'm').format('m');
      const h = mins === '0' ? moment(lastFixatge).add(1, 'h').format('H') : moment(lastFixatge).format('H');
      return { hores: h, minuts: mins };
    }
    return { hores: 7, minuts: 0 };
  }, [detallDiaFinal?.fixatges]);

  useEffect(() => {
    if (msgOk) setTimeout(() => dispatch(esborrarOkAction()), 10000);
    if (ui.msgOkError) setTimeout(() => dispatch(esborrarErrorOkAction()), 10000);
    if (ui.msgError) setTimeout(() => dispatch(esborrarErrorAction()), 10000);
  }, [msgOk, ui.msgOkError, ui.msgError, dispatch]);

  const handleChangeIncidencia = () => {
    if (incidencia) dispatch(uiAmagarIncidenciesAction());
    else dispatch(uiMostraIncidenciesAction());
    dispatch(uiAmagarIncidenciesRestaHoresAction());
    dispatch(uiAmagarIncidenciesRestaHoresFiJornadaAction());
  };
  const handleChangeIncidencia2 = () => {
    if (incidenciaRestaHores) dispatch(uiAmagarIncidenciesRestaHoresAction());
    else dispatch(uiMostraIncidenciesRestaHoresAction());
    dispatch(uiAmagarIncidenciesAction());
    dispatch(uiAmagarIncidenciesRestaHoresFiJornadaAction());
  };
  const handleChangeObrirFormRestaFiJornada = () => {
    if (incidenciaRestaHoresFiJornada) dispatch(uiAmagarIncidenciesRestaHoresFiJornadaAction());
    else dispatch(uiMostraIncidenciesRestaHoresFiJornadaAction());
    dispatch(uiAmagarIncidenciesAction());
    dispatch(uiAmagarIncidenciesRestaHoresAction());
  };

  // Si estem carregant → spinner
  if (!detallDia && calendari.loadingDetallMes) return <SpinerLoader />;

  return (
    <div>
      <h3 className='mt-3 text-aqua'>{t('Header.DetallDia')} <Moment format="DD/MM/YYYY" date={diaConsultat.dia} /></h3>

      {diaConsultatAlerta === 'capSetm' && !algunFixatge ? <p>{t('Calendari.Finde')}</p>
        : diaConsultatAlerta === 1 && !algunFixatge ? <p>{t('Calendari.DiaFestiu')}</p>
        : diaConsultatAlerta === 2 && !algunFixatge ? <p>{t('Calendari.Vacances')}</p>
        : (
          <>
            <p className="mt-3"><strong>{t('ControlPresencia_LlistatFixatgesDia.title')} <Moment format="DD/MM/YYYY" date={diaConsultat.dia} /></strong></p>
            <ul className='list-checked list-checked-soft-bg-primary list-checked-lg mb-6 mt-4'>
              {listItems.length ? listItems : <p>{t('Calendari.CapFixatge')}</p>}
            </ul>
            <hr />
            <h5>{t('FormulariIncidencies.title')}</h5>
            <ul className='list-checked list-checked-soft-bg-danger mb-6 mt-4'>
              {listInci.length ? listInci : <p className='alert alert-soft-dark'>{t('Calendari.CapIncidencia')}</p>}
            </ul>
            <hr />
            {msgOk && <div className="alert alert-success" role="alert">{msgOk}</div>}
            <p><a href="#!" onClick={handleChangeIncidencia}>{t('FormulariFiIniciJornada.PregIncidencia')}</a></p>
            {mesFixatges && <p><a href="#!" onClick={handleChangeIncidencia2}>{t('FormulariFiIniciJornada.PregIncidenciaResta')}</a></p>}
            {mesFixatges && <p><a href="#!" onClick={handleChangeObrirFormRestaFiJornada}>{t('FormulariFiIniciJornada.PregIncidenciaRestaFiJornada')}</a></p>}
            {incidencia && <FormulariIncidencia diaConsultat={diaConsultat.dia} hores={hores} minuts={minuts} />}
            {incidenciaRestaHores && <FormulariIncidenciaRestaHores diaConsultat={diaConsultat.dia} />}
            {incidenciaRestaHoresFiJornada && <FormulariIncidenciaRestaHoresFiJornada diaConsultat={diaConsultat.dia} />}
            <hr />
            <ResumHoresDiaConsultat dataAvui={dataAvui} />
            <hr />
          </>
        )}

      <div className='align-right'>
        <button className='btn btn-primary btn-sm m-1 mb-4' onClick={() => {
          dispatch(uiAmagarDiaConsultatAction());
          if (incidencia || incidenciaRestaHores) {
            dispatch(uiAmagarIncidenciesRestaHoresAction());
            dispatch(uiAmagarIncidenciesAction());
            dispatch(esborrarErrorAction());
          }
          if (ui.modalCalendar) dispatch(uiAmagarCalendarModal());
        }}>
          <i className="bi bi-arrow-left me-2 mb-4"></i> Tornar
        </button>
      </div>
    </div>
  );
};